import React, { useState, useEffect, Fragment } from 'react';
import { getFirestore, collection, query, where, getDocs, updateDoc } from 'firebase/firestore';
import { Menu, Transition } from '@headlessui/react';
import { EllipsisVerticalIcon } from '@heroicons/react/20/solid';
import { Link, useNavigate } from 'react-router-dom';

const statuses = {
  Complete: 'text-green-700 bg-green-50 ring-green-600/20',
  'In progress': 'text-gray-600 bg-gray-50 ring-gray-500/10',
  'Not started': 'text-red-700 bg-red-50 ring-red-600/20',
};

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const FormEditor = ({ currentUser, onFormDataChange }) => {
  const [formData, setFormData] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const checkFormStatus = (formData) => {
    if (!formData) return 'Not started';

    const fieldsToCheck = [
      'businessAge',
      'businessState',
      'email',
      'firstName',
      'lastName',
      'industry.code',
      'industry.description',
      'loanSize',
      'number'
    ];

    let isComplete = true;
    let isEmpty = true;

    for (let field of fieldsToCheck) {
      const fieldValue = field.split('.').reduce((obj, key) => obj?.[key], formData);
      if (!fieldValue) {
        isComplete = false;
      } else {
        isEmpty = false;
      }
    }

    if (isComplete) return 'Complete';
    if (isEmpty) return 'Not started';
    return 'In progress';
  };

  useEffect(() => {
    if (formData) {
      const updatedStatus = checkFormStatus(formData);
      setFormData((prevData) => ({
        ...prevData,
        status: updatedStatus,
      }));

      onFormDataChange({
        firstName: formData.firstName,
        industryCode: formData.industry?.code || '',
        dealInfo: formData,
      });
    }
  }, [formData, onFormDataChange]);

  useEffect(() => {
    const fetchFormData = async () => {
      if (currentUser) {
        try {
          const db = getFirestore();
          const formQuery = query(collection(db, 'loanApplications'), where('userId', '==', currentUser.uid));
          const querySnapshot = await getDocs(formQuery);
          if (!querySnapshot.empty) {
            const formDoc = querySnapshot.docs[0];
            setFormData({
              ...formDoc.data(),
            });
          } else {
            setFormData({
              name: 'Loan Application',
              status: 'Not started',
              href: '#',
            });
          }
        } catch (error) {
          console.error('Error fetching form data:', error);
          setError('Error fetching form data');
        }
      }
      setLoading(false);
    };

    fetchFormData();
  }, [currentUser]);

  const handleEdit = () => {
    if (!formData.loanSize) {
      navigate('/loanform'); // Replace '/loan-form' with the actual form route
    } else {
      setEditMode(true);
    }
  };

  const handleSave = async () => {
    if (currentUser) {
      const db = getFirestore();
      const formQuery = query(collection(db, 'loanApplications'), where('userId', '==', currentUser.uid));
      const querySnapshot = await getDocs(formQuery);
      if (!querySnapshot.empty) {
        const formDoc = querySnapshot.docs[0];
        await updateDoc(formDoc.ref, formData);
        setEditMode(false);
      }
    }
  };

  const handleCancel = () => {
    setEditMode(false);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleCapTableChange = (index, field, value) => {
    setFormData((prevData) => {
      const updatedCapTable = [...prevData.capTable];
      updatedCapTable[index] = {
        ...updatedCapTable[index],
        [field]: value,
      };
      return {
        ...prevData,
        capTable: updatedCapTable,
      };
    });
  };

  const handleAddCapTableRow = () => {
    setFormData((prevData) => ({
      ...prevData,
      capTable: [...prevData.capTable, { name: '', percentage: '' }],
    }));
  };

  const handleDeleteCapTableRow = (index) => {
    setFormData((prevData) => {
      const updatedCapTable = prevData.capTable.filter((_, i) => i !== index);
      return {
        ...prevData,
        capTable: updatedCapTable,
      };
    });
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  if (!formData) {
    return <div>No form data available</div>;
  }

  return (
    <div className="container mx-auto my-8">
    
      <div className="bg-white rounded-lg shadow-md p-6">
        {!editMode ? (
          <ul role="list" className="divide-y divide-gray-100">
            <li className="flex items-center justify-between gap-x-6 py-5">
              <div className="min-w-0">
                <div className="flex items-start gap-x-3">
                  <p className="text-sm font-semibold leading-6 text-gray-900">
                    <strong>Loan Application</strong>
                  </p>
                  <p
                    className={classNames(
                      statuses[formData.status],
                      'rounded-md whitespace-nowrap mt-0.5 px-1.5 py-0.5 text-xs font-medium ring-1 ring-inset'
                    )}
                  >
                    {formData.status}
                  </p>
                </div>
              </div>
              <div className="flex flex-none items-center gap-x-4">
                <button
                  onClick={handleEdit}
                  className="hidden rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:block"
                >
                  View application
                </button>
                <Menu as="div" className="relative flex-none">
                  <Menu.Button className="-m-2.5 block p-2.5 text-gray-500 hover:text-gray-900">
                    <span className="sr-only">Open options</span>
                    <EllipsisVerticalIcon className="h-5 w-5" aria-hidden="true" />
                  </Menu.Button>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute right-0 z-10 mt-2 w-32 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
                      <Menu.Item>
                        {({ active }) => (
                          <a
                            href="#"
                            className={classNames(
                              active ? 'bg-gray-50' : '',
                              'block px-3 py-1 text-sm leading-6 text-gray-900'
                            )}
                          >
                            Edit<span className="sr-only">, Loan Application</span>
                          </a>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active }) => (
                          <a
                            href="#"
                            className={classNames(
                              active ? 'bg-gray-50' : '',
                              'block px-3 py-1 text-sm leading-6 text-gray-900'
                            )}
                          >
                            Move<span className="sr-only">, Loan Application</span>
                          </a>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active }) => (
                          <a
                            href="#"
                            className={classNames(
                              active ? 'bg-gray-50' : '',
                              'block px-3 py-1 text-sm leading-6 text-gray-900'
                            )}
                          >
                            Delete<span className="sr-only">, Loan Application</span>
                          </a>
                        )}
                      </Menu.Item>
                    </Menu.Items>
                  </Transition>
                </Menu>
              </div>
            </li>
          </ul>
        ) : (
          <div className="mt-6">
            <h1 className="text-2xl font-bold mb-4">Form Editor</h1>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="businessAge" className="block font-medium mb-2">
                  Business Age
                </label>
                <input
                  type="text"
                  id="businessAge"
                  name="businessAge"
                  value={formData.businessAge || ''}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label htmlFor="businessState" className="block font-medium mb-2">
                  Business State
                </label>
                <input
                  type="text"
                  id="businessState"
                  name="businessState"
                  value={formData.businessState || ''}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label htmlFor="capTable" className="block font-medium mb-2">
                  Cap Table
                </label>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                      <tr>
                        <th className="px-6 py-3 bg-gray-100 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Name
                        </th>
                        <th className="px-6 py-3 bg-gray-100 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Percentage
                        </th>
                        <th className="px-6 py-3 bg-gray-100 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"></th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {formData.capTable.map((row, index) => (
                        <tr key={index}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <input
                              type="text"
                              value={row.name}
                              onChange={(event) =>
                                handleCapTableChange(index, 'name', event.target.value)
                              }
                              className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <input
                              type="text"
                              value={row.percentage}
                              onChange={(event) =>
                                handleCapTableChange(index, 'percentage', event.target.value)
                              }
                              className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <button
                              onClick={() => handleDeleteCapTableRow(index)}
                              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-500 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button
                  onClick={handleAddCapTableRow}
                  className="mt-2 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-500 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Add Cap Table Row
                </button>
              </div>
              <div>
                <label htmlFor="email" className="block font-medium mb-2">
                  Email
                </label>
                <input
                  type="text"
                  id="email"
                  name="email"
                  value={formData.email || ''}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label htmlFor="firstName" className="block font-medium mb-2">
                  First Name
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName || ''}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label htmlFor="lastName" className="block font-medium mb-2">
                  Last Name
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName || ''}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label htmlFor="industryCode" className="block font-medium mb-2">
                  Industry Code
                </label>
                <input
                  type="text"
                  id="industryCode"
                  name="industry.code"
                  value={formData.industry?.code || ''}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label htmlFor="industryDescription" className="block font-medium mb-2">
                  Industry Description
                </label>
                <input
                  type="text"
                  id="industryDescription"
                  name="industry.description"
                  value={formData.industry?.description || ''}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label htmlFor="loanPurpose" className="block font-medium mb-2">
                  Loan Purpose
                </label>
                <input
                  type="text"
                  id="loanPurpose"
                  name="loanPurpose"
                  value={formData.loanPurpose || ''}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label htmlFor="loanSize" className="block font-medium mb-2">
                  Loan Size
                </label>
                <input
                  type="text"
                  id="loanSize"
                  name="loanSize"
                  value={formData.loanSize || ''}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label htmlFor="number" className="block font-medium mb-2">
                  Number
                </label>
                <input
                  type="text"
                  id="number"
                  name="number"
                  value={formData.number || ''}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end">
              <button
                onClick={handleSave}
                className="inline-flex items-center px-4 py-2 mr-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-500 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Save
              </button>
              <button
                onClick={handleCancel}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-gray-500 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FormEditor;
